import { useEffect } from "react";
import { EmbeddedVideoSource } from "../types";

export interface VolumeChangeEventData {
  muted: boolean;
  volume: number;
}

export interface YouTubeVolumeChangeEvent {
  target: object;
  data: VolumeChangeEventData & { unstorable?: boolean };
}

interface VideoSourceConfig {
  eventName: string;
  subscribeMethod: string;
  unsubscribeMethod: string;
}

// Event listeners config per each video source.
const sourceConfig: Record<EmbeddedVideoSource, VideoSourceConfig> = {
  [EmbeddedVideoSource.YouTube]: {
    eventName: "onVolumeChange",
    subscribeMethod: "addEventListener",
    unsubscribeMethod: "removeEventListener",
  },
  [EmbeddedVideoSource.Vidyard]: {
    eventName: "volumeChange",
    subscribeMethod: "on",
    unsubscribeMethod: "off",
  },
  [EmbeddedVideoSource.Vimeo]: {
    eventName: "volumechange",
    subscribeMethod: "on",
    unsubscribeMethod: "off",
  },
  [EmbeddedVideoSource.Wistia]: {
    eventName: "volumechange",
    subscribeMethod: "on",
    unsubscribeMethod: "off",
  },
};

/* istanbul ignore next */
const useEmbeddedOnVolumeChange = (
  source: EmbeddedVideoSource,
  // @ts-ignore
  internalPlayer: any,
  onVolumeChange: (e: YouTubeVolumeChangeEvent | VolumeChangeEventData | number) => void,
) => {
  useEffect(() => {
    const config = sourceConfig[source];
    if (!internalPlayer || !config) return;

    const subscribeOnVolumeChange = () => {
      const { eventName, subscribeMethod } = config;

      if (internalPlayer[subscribeMethod]) {
        internalPlayer[subscribeMethod](eventName, onVolumeChange);
      }
    };

    const unsubscribeOnVolumeChange = () => {
      const { eventName, unsubscribeMethod } = config;

      if (internalPlayer[unsubscribeMethod]) {
        internalPlayer[unsubscribeMethod](eventName, onVolumeChange);
      }
    };

    subscribeOnVolumeChange();

    return () => {
      unsubscribeOnVolumeChange();
    };
  }, [internalPlayer, onVolumeChange, source]);
};

export default useEmbeddedOnVolumeChange;
